/**
 * `0-25/200` is an example value that is send in `content-range` header.
 *
 * 0 - `from` number.
 * 25 - `to` number, also the number of items per page.
 * 200 - `end` number. The number of items that are available without pagination.
 */
export interface Pagination {
  /** First order number from range of currently fetched contents page */
  from: number;

  /** Last order number from range of currently fetched contents page */
  to: number;

  /** Number of items available without pagination */
  end: number;

  /** Items per page (usually different than `to - from` on the last page) */
  perPage: number;
}

export interface PaginationInfo extends Pagination {
  /** Current page (starting at 0) */
  page: number;

  /** Total page count */
  pageCount: number;

  /** Is there another page next? (page < pageCount - 1) */
  next: boolean;

  /** Is there another page next? (page > 0) */
  prev: boolean;
}

export type DisplayPaginationRange = ("dots" | number)[];

/**
 * Replaces deprecated `prevContent` and `nextContent`.
 * Adds additional pagination info after contents fetch.
 * */
export const paginationInfo = ({ from, to, end, perPage }: Pagination): PaginationInfo => {
  const page = Math.floor(from / perPage);
  const pageCount = Math.ceil(end / perPage);

  return {
    from,
    to,
    end,
    perPage,

    page,
    pageCount,

    next: page < pageCount - 1,
    prev: page > 0,
  };
};

export const paginationDisplayRange = (pagination: Pagination | PaginationInfo, range = 2) => {
  const { page, pageCount } = "page" in pagination ? pagination : paginationInfo(pagination);

  const startIndex = Math.max(0, page - range);
  const endIndex = Math.min(pageCount - 1, page + range);

  const r: DisplayPaginationRange = [];

  if (page >= range + 1) {
    r.push(0);
  }

  if (page >= range + 2) {
    r.push("dots");
  }

  for (let i = startIndex; i <= endIndex; i++) {
    r.push(i);
  }

  if (page < pageCount - (range + 2)) {
    r.push("dots");
  }

  if (page < pageCount - (range + 1)) {
    r.push(pageCount - 1);
  }

  return r;
};
